import React, { useLayoutEffect, useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  makeStyles,
  mergeClasses,
  tokens,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableCellLayout,
  Spinner,
  Text,
  Card,
} from "@fluentui/react-components";
import {
  ArrowSortRegular,
  ArrowSortUpRegular,
  ArrowSortDownRegular,
} from "@fluentui/react-icons";
import {
  GridRow,
  MetricType,
  YearType,
  SortConfig,
  METRIC_LABELS,
  METRIC_SHORT_LABELS,
  DIMENSION_LABELS,
  DimensionType,
} from "../types";

const useStyles = makeStyles({
  card: {
    flexGrow: 0,
    flexShrink: 1,
    minHeight: 0,
    overflow: "hidden",
    padding: 0,
  },
  tableContainer: {
    overflow: "auto",
    flexGrow: 1,
    minHeight: 0,
    position: "relative",
    WebkitOverflowScrolling: "touch",
  },
  table: {
    tableLayout: "fixed",
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: "100%",
  },
  headerRow: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    boxShadow: `0 3px 4px -2px ${tokens.colorNeutralShadowAmbient}`,
  },
  headerCell: {
    height: "46px",
    boxSizing: "border-box",
    fontWeight: tokens.fontWeightSemibold,
    backgroundColor: tokens.colorNeutralBackground1,
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  headerButton: {
    font: "inherit",
    color: "inherit",
    border: 0,
    backgroundColor: "transparent",
    padding: 0,
    width: "100%",
    textAlign: "left",
    cursor: "pointer",
  },
  headerContent: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
  },
  headerLabel: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  headerMetric: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  headerYear: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    "&:focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  dimensionCell: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
    whiteSpace: "nowrap",
    paddingTop: 0,
    paddingBottom: 0,
  },
  valueCell: {
    textAlign: "right",
    whiteSpace: "nowrap",
    paddingTop: 0,
    paddingBottom: 0,
    overflow: "hidden",
  },
  dimensionLabel: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  itemLine: {
    height: "20px",
    lineHeight: "20px",
  },
  itemCode: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground3,
  },
  spacerCell: {
    padding: 0,
    border: 0,
  },
  totalCell: {
    position: "sticky",
    bottom: 0,
    zIndex: 1,
    height: "40px",
    backgroundColor: tokens.colorNeutralBackground1,
    borderTop: `2px solid ${tokens.colorNeutralStroke1}`,
  },
  loadingContainer: {
    padding: tokens.spacingVerticalXXL,
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    justifyContent: "center",
    alignItems: "center",
    gap: tokens.spacingVerticalM,
  },
  emptyContainer: {
    padding: tokens.spacingVerticalXXL,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexGrow: 1,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
  totalRow: {
    fontWeight: tokens.fontWeightBold,
    borderTop: `2px solid ${tokens.colorNeutralStroke1}`,
    //backgroundColor: tokens.colorNeutralBackground3,
  },
  totalDimensionCell: {
    fontWeight: tokens.fontWeightBold,
    minWidth: "120px",
    whiteSpace: "nowrap",
  },
});

interface DataGridProps {
  data: GridRow[];
  selectedMetrics: MetricType[];
  currentDimension: DimensionType;
  sortConfig: SortConfig | null;
  onSort: (column: string) => void;
  onRowClick: (row: GridRow) => void;
  isLoading?: boolean;
}

const YEARS: YearType[] = ["PPY", "PY", "CY"];
const ROW_HEIGHT = 40;
const ITEM_ROW_HEIGHT = 56;
const HEADER_HEIGHT = 56;
const DIMENSION_WIDTH = 240;
const METRIC_WIDTH = 150;
const CURRENCY_FORMATTER = new Intl.NumberFormat("nl-BE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const QUANTITY_FORMATTER = new Intl.NumberFormat("nl-BE");
const CURRENCY_METRICS: MetricType[] = [
  "revenue",
  "revenueDiffEuro",
  "cost",
  "costDiffEuro",
  "profit",
  "profitDiffEuro",
];

const ACTUAL_YEARS: Record<YearType, number> = {
  CY: new Date().getFullYear(),
  PY: new Date().getFullYear() - 1,
  PPY: new Date().getFullYear() - 2,
};

const isPercentageMetric = (metric: MetricType): boolean =>
  metric.includes("Percent") || metric === "margin";

const formatValue = (value: number | undefined, metric: MetricType): string => {
  if (value === undefined || value === null) return "-";

  if (isPercentageMetric(metric)) {
    return `${value.toFixed(1)}%`;
  }

  if (CURRENCY_METRICS.includes(metric)) {
    return CURRENCY_FORMATTER.format(value);
  }

  if (metric === "quantity" || metric === "quantityDiffNumber") {
    return QUANTITY_FORMATTER.format(value);
  }

  return value.toString();
};

const SortIcon: React.FC<{ sortConfig: SortConfig | null; column: string }> = ({
  sortConfig,
  column,
}) => {
  if (sortConfig?.column !== column) {
    return <ArrowSortRegular />;
  }
  return sortConfig.direction === "asc" ? (
    <ArrowSortUpRegular />
  ) : (
    <ArrowSortDownRegular />
  );
};

export const DataGrid = React.memo(function DataGrid({
  data,
  selectedMetrics,
  currentDimension,
  sortConfig,
  onSort,
  onRowClick,
  isLoading = false,
}: DataGridProps) {
  const styles = useStyles();
  const scrollRef = useRef<HTMLDivElement>(null);
  const rowHeight = currentDimension === "item" ? ITEM_ROW_HEIGHT : ROW_HEIGHT;
  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    getItemKey: (index) => data[index].id,
    scrollMargin: HEADER_HEIGHT,
    overscan: 8,
  });
  const virtualRows = virtualizer.getVirtualItems();
  const paddingTop =
    virtualRows.length > 0 ? virtualRows[0].start - HEADER_HEIGHT : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? virtualizer.getTotalSize() -
        (virtualRows[virtualRows.length - 1].end - HEADER_HEIGHT)
      : 0;

  useLayoutEffect(() => {
    virtualizer.measure();
  }, [virtualizer, rowHeight]);

  useLayoutEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [data, currentDimension, sortConfig, isLoading]);

  const totals = useMemo(() => {
    const result: Record<string, number | undefined> = {};
    selectedMetrics.forEach((metric) => {
      YEARS.forEach((year) => {
        const key = `${metric}-${year}`;
        let sum = 0;
        let count = 0;
        for (const row of data) {
          const value = row.metrics[year]?.[metric];
          if (value !== undefined && value !== null) {
            sum += value;
            count++;
          }
        }
        if (count === 0) {
          result[key] = undefined;
        } else if (isPercentageMetric(metric)) {
          result[key] = sum / count;
        } else {
          result[key] = sum;
        }
      });
    });
    return result;
  }, [data, selectedMetrics]);

  const columns = useMemo(() => {
    const cols: {
      key: string;
      label: string;
      shortLabel?: string;
      yearLabel?: string;
      metric?: MetricType;
      year?: YearType;
    }[] = [{ key: "dimension", label: DIMENSION_LABELS[currentDimension] }];

    selectedMetrics.forEach((metric) => {
      YEARS.forEach((year) => {
        cols.push({
          key: `${metric}-${year}`,
          label: `${METRIC_LABELS[metric]} (${ACTUAL_YEARS[year]})`,
          shortLabel: METRIC_SHORT_LABELS[metric],
          yearLabel: String(ACTUAL_YEARS[year]),
          metric,
          year,
        });
      });
    });

    return cols;
  }, [selectedMetrics, currentDimension]);

  if (isLoading) {
    return (
      <Card appearance="subtle" className={styles.card}>
        <div className={styles.loadingContainer}>
          <Spinner size="small" />
          <Text>Loading data...</Text>
        </div>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card className={styles.card}>
        <div className={styles.emptyContainer}>
          <Text size={400}>No data available for the selected filters.</Text>
        </div>
      </Card>
    );
  }

  return (
    <Card className={styles.card}>
      <div ref={scrollRef} className={styles.tableContainer}>
        <Table
          aria-label="Sales data grid"
          aria-rowcount={data.length + 2}
          className={styles.table}
          style={{
            width: DIMENSION_WIDTH + (columns.length - 1) * METRIC_WIDTH,
          }}
        >
          <colgroup>
            {columns.map((col) => (
              <col
                key={col.key}
                style={{
                  width:
                    col.key === "dimension" ? DIMENSION_WIDTH : METRIC_WIDTH,
                }}
              />
            ))}
          </colgroup>
          <TableHeader>
            <TableRow aria-rowindex={1} className={styles.headerRow}>
              {columns.map((col) => (
                <TableHeaderCell
                  key={col.key}
                  className={styles.headerCell}
                  aria-sort={
                    sortConfig?.column === col.key
                      ? sortConfig.direction === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                  title={col.label}
                >
                  <button
                    type="button"
                    className={styles.headerButton}
                    onClick={() => onSort(col.key)}
                    aria-label={`Sort by ${col.label}`}
                  >
                    <div className={styles.headerContent}>
                      {col.shortLabel ? (
                        <span className={styles.headerLabel}>
                          <span className={styles.headerMetric}>
                            {col.shortLabel}
                          </span>
                          <span className={styles.headerYear}>
                            {col.yearLabel}
                          </span>
                        </span>
                      ) : (
                        <span>{col.label}</span>
                      )}
                      <SortIcon sortConfig={sortConfig} column={col.key} />
                    </div>
                  </button>
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {paddingTop > 0 && (
              <TableRow aria-hidden="true">
                <TableCell
                  colSpan={columns.length}
                  className={styles.spacerCell}
                  style={{ height: paddingTop }}
                />
              </TableRow>
            )}
            {virtualRows.map((virtualRow) => {
              const row = data[virtualRow.index];
              const articleLines =
                currentDimension === "item"
                  ? row.dimensionLabel.split(/\r?\n/)
                  : null;
              const description =
                articleLines && articleLines.length > 1
                  ? articleLines[0].trim() || "-"
                  : "-";
              const articleCode = articleLines?.slice(-1)[0];
              return (
                <TableRow
                  key={row.id}
                  data-row-id={row.id}
                  aria-rowindex={virtualRow.index + 2}
                  tabIndex={0}
                  className={styles.clickableRow}
                  style={{ height: rowHeight }}
                  onClick={() => onRowClick(row)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onRowClick(row);
                    }
                  }}
                >
                  <TableCell
                    className={styles.dimensionCell}
                    title={row.dimensionLabel}
                  >
                    {articleLines ? (
                      <>
                        <span
                          className={mergeClasses(
                            styles.dimensionLabel,
                            styles.itemLine,
                          )}
                        >
                          {description}
                        </span>
                        <span
                          className={mergeClasses(
                            styles.dimensionLabel,
                            styles.itemLine,
                            styles.itemCode,
                          )}
                        >
                          {articleCode}
                        </span>
                      </>
                    ) : (
                      <span className={styles.dimensionLabel}>
                        {row.dimensionLabel}
                      </span>
                    )}
                  </TableCell>
                  {selectedMetrics.map((metric) =>
                    YEARS.map((year) => (
                      <TableCell
                        key={`${row.id}-${metric}-${year}`}
                        className={styles.valueCell}
                      >
                        <TableCellLayout>
                          {formatValue(row.metrics[year]?.[metric], metric)}
                        </TableCellLayout>
                      </TableCell>
                    )),
                  )}
                </TableRow>
              );
            })}
            {paddingBottom > 0 && (
              <TableRow aria-hidden="true">
                <TableCell
                  colSpan={columns.length}
                  className={styles.spacerCell}
                  style={{ height: paddingBottom }}
                />
              </TableRow>
            )}
          </TableBody>
          <tfoot>
            <TableRow className={styles.totalRow}>
              <TableCell
                className={mergeClasses(
                  styles.totalDimensionCell,
                  styles.totalCell,
                )}
              >
                <TableCellLayout>Total</TableCellLayout>
              </TableCell>
              {selectedMetrics.map((metric) =>
                YEARS.map((year) => (
                  <TableCell
                    key={`total-${metric}-${year}`}
                    className={mergeClasses(styles.valueCell, styles.totalCell)}
                  >
                    <TableCellLayout>
                      {formatValue(totals[`${metric}-${year}`], metric)}
                    </TableCellLayout>
                  </TableCell>
                )),
              )}
            </TableRow>
          </tfoot>
        </Table>
      </div>
    </Card>
  );
});
